'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DownloadModal({ isOpen, onClose }: DownloadModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl modal-backdrop">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          aria-label="Close modal"
        >
          <Icon name="XMarkIcon" size={16} className="text-gray-600" />
        </button>

        {/* Header */}
        <div className="text-center mb-7">
          <div className="text-4xl mb-3">🧞</div>
          <h3 className="text-2xl font-black text-foreground mb-1">Get Summons</h3>
          <p className="text-sm text-muted">Be first in line when we launch.</p>
        </div>

        {/* Join Waitlist CTA */}
        <Link
          href="/waitlist"
          onClick={onClose}
          className="w-full flex items-center justify-center gap-2 bg-primary text-white px-5 py-4 rounded-2xl font-bold text-base mb-4 hover:bg-primary/90 transition-all"
        >
          <span>Join the Waitlist</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </Link>

        {/* Coming Soon Store Badges */}
        <div className="space-y-3 mb-6">
          <div className="w-full flex items-center gap-4 bg-foreground text-white px-5 py-4 rounded-2xl font-semibold opacity-60 cursor-not-allowed select-none">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            <div className="text-left">
              <div className="text-[10px] opacity-60 font-normal">Coming Soon to the</div>
              <div className="text-base font-bold leading-tight">App Store</div>
            </div>
          </div>

          <div className="w-full flex items-center gap-4 bg-gray-700 text-white px-5 py-4 rounded-2xl font-semibold opacity-60 cursor-not-allowed select-none">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.0989L4.841 5.4467a.4161.4161 0 00-.5677-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C3.8592 10.3902 2.3 12.3 2 14.6h20c-.3-2.3-1.8592-4.2098-4.1185-5.2786"/></svg>
            <div className="text-left">
              <div className="text-[10px] opacity-70 font-normal">Coming Soon on</div>
              <div className="text-base font-bold leading-tight">Google Play</div>
            </div>
          </div>
        </div>

        {/* QR Code */}
        <div className="hidden sm:flex items-center gap-3 p-4 bg-surface rounded-2xl border border-border">
          <div className="w-16 h-16 bg-foreground rounded-xl flex items-center justify-center flex-shrink-0">
            <svg width="52" height="52" viewBox="0 0 64 64" fill="none">
              <rect x="4" y="4" width="24" height="24" rx="2" fill="white"/>
              <rect x="8" y="8" width="16" height="16" rx="1" fill="#0A0A0A"/>
              <rect x="12" y="12" width="8" height="8" fill="white"/>
              <rect x="36" y="4" width="24" height="24" rx="2" fill="white"/>
              <rect x="40" y="8" width="16" height="16" rx="1" fill="#0A0A0A"/>
              <rect x="44" y="12" width="8" height="8" fill="white"/>
              <rect x="4" y="36" width="24" height="24" rx="2" fill="white"/>
              <rect x="8" y="40" width="16" height="16" rx="1" fill="#0A0A0A"/>
              <rect x="12" y="44" width="8" height="8" fill="white"/>
              <rect x="36" y="36" width="6" height="6" fill="white"/>
              <rect x="44" y="36" width="6" height="6" fill="white"/>
              <rect x="52" y="36" width="6" height="6" fill="white"/>
              <rect x="36" y="44" width="6" height="6" fill="white"/>
              <rect x="52" y="44" width="6" height="6" fill="white"/>
              <rect x="36" y="52" width="6" height="6" fill="white"/>
              <rect x="44" y="52" width="14" height="6" fill="white"/>
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold text-foreground">Coming soon to mobile</p>
            <p className="text-xs text-muted">Join the waitlist to be notified</p>
          </div>
        </div>
      </div>
    </div>
  );
}